import { useState, useEffect, useRef, useCallback } from "react";
import styles from "./StoryViewer.module.css";
import useDashboardStore from "../../store/dashboardStore";

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

function buildCaptionSegments(text) {
  const parts = text.split(URL_REGEX);
  return parts.map((part, i) =>
    URL_REGEX.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.captionLink}
        onClick={(e) => e.stopPropagation()}
      >
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

const IMAGE_DURATION = 5000; // ms for image / gif / text

const StoryViewer = () => {
  const { viewerStories, storiesViewerStartIndex, setShowStoriesViewer } =
    useDashboardStore();

  const stories = viewerStories;
  const [currentIndex, setCurrentIndex] = useState(storiesViewerStartIndex);
  const [progress, setProgress] = useState(0);

  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const touchStartYRef = useRef(null);
  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;

  const close = useCallback(() => {
    clearInterval(timerRef.current);
    setShowStoriesViewer(false);
  }, [setShowStoriesViewer]);

  const goNext = useCallback(() => {
    clearInterval(timerRef.current);
    if (currentIndexRef.current >= stories.length - 1) {
      setShowStoriesViewer(false);
    } else {
      setCurrentIndex((i) => i + 1);
    }
  }, [stories.length, setShowStoriesViewer]);

  const goPrev = useCallback(() => {
    clearInterval(timerRef.current);
    if (currentIndexRef.current > 0) {
      setCurrentIndex((i) => i - 1);
    }
  }, []);

  // Start the interval-based progress timer (for image / gif / text)
  const startTimer = useCallback(
    (duration) => {
      clearInterval(timerRef.current);
      setProgress(0);
      const tickMs = 100;
      const totalTicks = duration / tickMs;
      let ticks = 0;
      timerRef.current = setInterval(() => {
        ticks++;
        const pct = (ticks / totalTicks) * 100;
        if (pct >= 100) {
          clearInterval(timerRef.current);
          setProgress(100);
          goNext();
        } else {
          setProgress(pct);
        }
      }, tickMs);
    },
    [goNext]
  );

  // Reset + start whenever index changes
  useEffect(() => {
    clearInterval(timerRef.current);
    setProgress(0);

    const story = stories[currentIndex];
    if (!story) return;

    if (story.type !== "video") {
      startTimer(IMAGE_DURATION);
    }
    // Video: timer starts in onCanPlay below

    return () => clearInterval(timerRef.current);
  }, [currentIndex, stories, startTimer]);

  // --- Video handlers ---
  const handleCanPlay = () => {
    const video = videoRef.current;
    if (!video) return;
    video.play().catch(() => {});
    const duration = Math.min(video.duration * 1000, 120000);
    startTimer(duration);
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    const maxDuration = Math.min(video.duration, 120);
    setProgress(Math.min((video.currentTime / maxDuration) * 100, 100));
  };

  const handleVideoEnded = () => {
    clearInterval(timerRef.current);
    goNext();
  };

  // --- Swipe down to close ---
  const handleTouchStart = (e) => {
    touchStartYRef.current = e.touches[0].clientY;
  };
  const handleTouchEnd = (e) => {
    if (touchStartYRef.current === null) return;
    const delta = e.changedTouches[0].clientY - touchStartYRef.current;
    if (delta > 80) close();
    touchStartYRef.current = null;
  };

  // --- Keyboard support ---
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev, close]);

  if (!stories.length) return null;

  const story = stories[currentIndex];

  return (
    <div
      className={styles.overlay}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Progress bars */}
      <div className={styles.progressRow}>
        {stories.map((_, i) => (
          <div key={i} className={styles.progressSegment}>
            <div
              className={styles.progressFill}
              style={{
                width:
                  i < currentIndex
                    ? "100%"
                    : i === currentIndex
                    ? `${progress}%`
                    : "0%",
              }}
            />
          </div>
        ))}
      </div>

      {/* Close button */}
      <button className={styles.closeBtn} onClick={close}>
        ✕
      </button>

      {/* Content */}
      <div className={styles.contentArea}>
        {story.type === "text" ? (
          <div className={styles.textStory}>
            <p className={styles.textContent}>{story.content}</p>
          </div>
        ) : story.type === "video" ? (
          <video
            key={story.id}
            ref={videoRef}
            src={story.content}
            className={styles.videoMedia}
            playsInline
            onCanPlay={handleCanPlay}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnded}
          />
        ) : (
          <img
            key={story.id}
            src={story.content}
            alt="Story"
            className={styles.imageMedia}
          />
        )}
      </div>

      {/* Caption overlay */}
      {story.caption?.trim() && (
        <div className={styles.captionBar}>
          <div className={styles.captionScroll}>
            <p className={styles.captionText}>
              {buildCaptionSegments(story.caption)}
            </p>
          </div>
        </div>
      )}

      {/* Tap zones */}
      <div className={styles.tapLeft} onClick={goPrev} />
      <div className={styles.tapRight} onClick={goNext} />
    </div>
  );
};

export default StoryViewer;
